import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InputAdornment from '@mui/material/InputAdornment';

import { toast } from 'src/components/snackbar';
import { MESSAGES } from 'src/constants/messages';
import { useComboCoveragesById } from 'src/api/hooks/useEmployerDataQueries';
import { useMoveTargetCoverage } from 'src/api/hooks/usePolicyQueries';
import {
  useMovePolicyMutation,
  useMovePolicyToExistingCoverageMutation,
  useMovePolicyToNewComboMutation,
  useMovePoliciesBatchToExistingCoverageMutation,
  useMovePoliciesBatchToExistingComboMutation,
  useMovePoliciesBatchToNewComboMutation,
} from 'src/api/hooks/usePolicyMutations';

// ----------------------------------------------------------------------

type MoveType = 'existing' | 'new-coverage' | 'new-combo' | null;

const EXISTING_COVERAGE_MOVE_TYPE: Exclude<MoveType, null> = 'existing';

type MovePolicyModalProps = {
  open: boolean;
  onClose: () => void;
  comboId: number;
  coverageId: number;
  policyId?: number;
  policyIds?: number[];
};

type ApiErrorPayload = {
  message?: string;
  errors?: Array<{ message?: string }>;
};

type MutationError = {
  message?: string;
  response?: {
    data?: ApiErrorPayload;
  };
};

const FALLBACK_MOVE_ERROR = 'Failed to move the policy. Please try again.';

function getMovePolicyErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return FALLBACK_MOVE_ERROR;
  }

  const mutationError = error as MutationError;
  const responsePayload = mutationError.response?.data;
  const apiErrorMessage = responsePayload?.errors?.find((item) => Boolean(item?.message))?.message;

  if (apiErrorMessage && apiErrorMessage.trim().length > 0) {
    return apiErrorMessage;
  }

  if (responsePayload?.message && responsePayload.message.trim().length > 0) {
    return responsePayload.message;
  }

  if (mutationError.message && mutationError.message.trim().length > 0) {
    return mutationError.message;
  }

  return FALLBACK_MOVE_ERROR;
}

export function MovePolicyModal({ open, onClose, comboId, coverageId, policyId, policyIds }: MovePolicyModalProps) {
  const navigate = useNavigate();
  const isBatch = Boolean(policyIds && policyIds.length > 1);
  const effectivePolicyId = policyId ?? policyIds?.[0] ?? 0;
  const [moveType, setMoveType] = useState<MoveType>(null);
  const [existingCoverageId, setExistingCoverageId] = useState('');
  const [targetComboId, setTargetComboId] = useState('');
  const [useLinkedCombo, setUseLinkedCombo] = useState(false);
  const [reason, setReason] = useState('');
  const [comboIdError, setComboIdError] = useState('');
  const [existingCoverageIdError, setExistingCoverageIdError] = useState('');
  const [reasonError, setReasonError] = useState('');

  const numericComboId = targetComboId.length === 7 ? Number(targetComboId) : 0;
  const numericExistingCoverageId = existingCoverageId.length === 8 ? Number(existingCoverageId) : 0;

  const comboQuery = useComboCoveragesById(numericComboId);
  const existingCoverageQuery = useMoveTargetCoverage(
    comboId,
    coverageId,
    effectivePolicyId,
    numericExistingCoverageId
  );

  const moveMutation = useMovePolicyMutation();
  const moveToNewComboMutation = useMovePolicyToNewComboMutation();
  const moveToExistingCoverageMutation = useMovePolicyToExistingCoverageMutation();
  const batchToExistingCoverageMutation = useMovePoliciesBatchToExistingCoverageMutation();
  const batchToExistingComboMutation = useMovePoliciesBatchToExistingComboMutation();
  const batchToNewComboMutation = useMovePoliciesBatchToNewComboMutation();

  const resolvedComboName = comboQuery.data?.data?.controllingCoverage?.comboName ?? null;
  const hasResolvedCombo = Boolean(resolvedComboName);
  const hasComboIdInput = targetComboId.length > 0;
  const hasInvalidComboIdLength = hasComboIdInput && targetComboId.length !== 7;
  const hasMissingComboLookupResult =
    targetComboId.length === 7 &&
    !comboQuery.isLoading &&
    comboQuery.isSuccess &&
    !hasResolvedCombo;

  const resolvedTargetCoverageName = existingCoverageQuery.data?.data?.coverageName ?? null;
  const resolvedTargetComboId = existingCoverageQuery.data?.data?.comboId ?? null;
  const resolvedTargetComboName = existingCoverageQuery.data?.data?.comboName ?? null;

  const hasExistingCoverageIdInput = existingCoverageId.length > 0;
  const hasInvalidExistingCoverageIdLength = hasExistingCoverageIdInput && existingCoverageId.length !== 8;
  const hasMissingCoverageLookupResult =
    existingCoverageId.length === 8 &&
    !existingCoverageQuery.isLoading &&
    existingCoverageQuery.isSuccess &&
    !resolvedTargetComboId;

  // Set error when query fails
  const comboValidationError =
    hasMissingComboLookupResult ? 'Enter a valid combo ID.' :
    comboQuery.isError ? 'Enter a valid combo ID.' :
    comboIdError;

  const existingCoverageValidationError =
    hasMissingCoverageLookupResult ? 'Enter a valid coverage ID' :
    existingCoverageQuery.isError ? 'Enter a valid coverage ID' :
    existingCoverageIdError;

  const resetForm = useCallback(() => {
    setMoveType(null);
    setExistingCoverageId('');
    setTargetComboId('');
    setUseLinkedCombo(false);
    setReason('');
    setComboIdError('');
    setExistingCoverageIdError('');
    setReasonError('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleMoveTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextMoveType = event.target.value as MoveType;

    setMoveType(nextMoveType);

    if (nextMoveType !== 'existing') {
      setExistingCoverageId('');
    }

    if (nextMoveType !== 'new-coverage') {
      setTargetComboId('');
      setUseLinkedCombo(false);
    }

    setComboIdError('');
    setExistingCoverageIdError('');
    setReasonError('');
  };

  const handleExistingCoverageIdChange = (value: string) => {
    const numericOnly = value.replace(/\D/g, '').slice(0, 8);
    setExistingCoverageId(numericOnly);
    setExistingCoverageIdError('');
  };

  const handleComboIdChange = (value: string) => {
    const numericOnly = value.replace(/\D/g, '').slice(0, 7);
    setTargetComboId(numericOnly);
    setComboIdError('');
  };

  const handleReasonChange = (value: string) => {
    setReason(value);
    setReasonError('');
  };

  const validateReason = () => {
    if (reason.trim().length === 0) {
      setReasonError('Reason is required.');
      return false;
    }

    setReasonError('');
    return true;
  };

  const validateExistingCoverage = (showEmptyError = false) => {
    if (moveType !== 'existing') return true;

    if (existingCoverageId.length === 0) {
      if (showEmptyError) {
        setExistingCoverageIdError('Enter a valid coverage ID');
        return false;
      }

      setExistingCoverageIdError('');
      return true;
    }

    if (existingCoverageId.length !== 8) {
      setExistingCoverageIdError('Enter a valid coverage ID');
      return false;
    }

    if (!existingCoverageQuery.isLoading && resolvedTargetComboId === null) {
      setExistingCoverageIdError('Enter a valid coverage ID');
      return false;
    }

    setExistingCoverageIdError('');
    return true;
  };

  const validateTargetCombo = (showEmptyError = false) => {
    if (moveType !== 'new-coverage') return true;

    if (targetComboId.length === 0) {
      if (showEmptyError) {
        setComboIdError('Enter a valid combo ID.');
        return false;
      }

      setComboIdError('');
      return true;
    }

    if (targetComboId.length !== 7) {
      setComboIdError('Enter a valid combo ID.');
      return false;
    }

    if (!comboQuery.isLoading && !hasResolvedCombo) {
      setComboIdError('Enter a valid combo ID.');
      return false;
    }

    setComboIdError('');
    return true;
  };

  const handleUseLinkedComboChange = (checked: boolean) => {
    setUseLinkedCombo(checked);
    if (checked) {
      setTargetComboId(String(comboId));
      setComboIdError('');
    } else {
      setTargetComboId('');
    }
  };

  const subjectLabel = isBatch ? `${policyIds!.length} policies` : 'Policy';
  const systemNotePrefix = moveType === 'existing'
    ? `${subjectLabel} moved from Coverage ${coverageId} to ${existingCoverageId || '##'}`
    : `${subjectLabel} moved from Coverage ${coverageId} to new coverage`;

  const isSaving =
    moveMutation.isPending ||
    moveToNewComboMutation.isPending ||
    moveToExistingCoverageMutation.isPending ||
    batchToExistingCoverageMutation.isPending ||
    batchToExistingComboMutation.isPending ||
    batchToNewComboMutation.isPending;

  const successToastMessage = isBatch
    ? `${policyIds!.length} policies were successfully moved`
    : 'The policy was successfully moved';

  const handleSave = () => {
    if (isSaving) return;

    if (!moveType) return;

    if (isBatch && (!policyIds || policyIds.length === 0)) return;

    const isReasonValid = validateReason();

    const fullNote = `${systemNotePrefix}. ${reason.trim()}`;

    if (moveType === 'existing') {
      const isExistingCoverageValid = validateExistingCoverage(true);

      if (!isReasonValid || !isExistingCoverageValid) {
        return;
      }

      if (isBatch) {
        batchToExistingCoverageMutation.mutate(
          {
            comboId,
            coverageId,
            policyIds: policyIds!,
            targetCoverageId: Number(existingCoverageId),
            note: fullNote,
          },
          {
            onSuccess: (data) => {
              toast.success(successToastMessage);
              handleClose();
              navigate(
                `/dashboard/employer-data/combo/${data.newComboId}/coverage/${data.newCoverageId}`,
                { replace: true }
              );
            },
            onError: (error) => {
              toast.error(getMovePolicyErrorMessage(error));
            },
          }
        );
      } else {
        moveToExistingCoverageMutation.mutate(
          {
            comboId,
            coverageId,
            policyId: effectivePolicyId,
            targetCoverageId: Number(existingCoverageId),
            note: fullNote,
          },
          {
            onSuccess: (data) => {
              toast.success(successToastMessage);
              handleClose();
              navigate(
                `/dashboard/employer-data/combo/${data.newComboId}/coverage/${data.newCoverageId}`,
                { replace: true }
              );
            },
            onError: (error) => {
              toast.error(getMovePolicyErrorMessage(error));
            },
          }
        );
      }

      return;
    }

    if (moveType === 'new-combo') {
      if (!isReasonValid) return;

      if (isBatch) {
        batchToNewComboMutation.mutate(
          {
            comboId,
            coverageId,
            policyIds: policyIds!,
            note: fullNote,
          },
          {
            onSuccess: (data) => {
              toast.success(successToastMessage);
              handleClose();
              navigate(
                `/dashboard/employer-data/combo/${data.newComboId}/coverage/${data.newCoverageId}`,
                { replace: true }
              );
            },
            onError: (error) => {
              toast.error(getMovePolicyErrorMessage(error));
            },
          }
        );
      } else {
        moveToNewComboMutation.mutate(
          {
            comboId,
            coverageId,
            policyId: effectivePolicyId,
            note: fullNote,
          },
          {
            onSuccess: (data) => {
              toast.success(successToastMessage);
              handleClose();
              navigate(
                `/dashboard/employer-data/combo/${data.newComboId}/coverage/${data.newCoverageId}`,
                { replace: true }
              );
            },
            onError: (error) => {
              toast.error(getMovePolicyErrorMessage(error));
            },
          }
        );
      }
      return;
    }

    const isTargetComboValid = validateTargetCombo(true);
    if (!isReasonValid || !isTargetComboValid) return;

    if (isBatch) {
      batchToExistingComboMutation.mutate(
        {
          comboId,
          coverageId,
          policyIds: policyIds!,
          targetComboId: Number(targetComboId),
          note: fullNote,
        },
        {
          onSuccess: (data) => {
            toast.success(successToastMessage);
            handleClose();
            navigate(
              `/dashboard/employer-data/combo/${data.newComboId}/coverage/${data.newCoverageId}`,
              { replace: true }
            );
          },
          onError: (error) => {
            toast.error(getMovePolicyErrorMessage(error));
          },
        }
      );
    } else {
      moveMutation.mutate(
        {
          comboId,
          coverageId,
          policyId: effectivePolicyId,
          targetComboId: Number(targetComboId),
          note: fullNote,
        },
        {
          onSuccess: (data) => {
            toast.success(successToastMessage);
            handleClose();
            navigate(
              `/dashboard/employer-data/combo/${data.newComboId}/coverage/${data.newCoverageId}`,
              { replace: true }
            );
          },
          onError: (error) => {
            toast.error(getMovePolicyErrorMessage(error));
          },
        }
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      data-testid="move-policy-modal"
      sx={{
        '& .MuiDialog-paper': { maxWidth: 720 },
        '& .MuiDialogTitle-root + .MuiDialogContent-root': { pt: 3 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '1px dashed',
          borderColor: 'divider',
        }}
      >
        {isBatch ? `Move ${policyIds!.length} policies` : 'Move policy'}
        <IconButton
          onClick={handleClose}
          aria-label="close"
          data-testid="move-policy-close-btn"
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 0 }}>
        <RadioGroup value={moveType ?? ''} onChange={handleMoveTypeChange}>
          {/* Option 1: Existing coverage and combo */}
          <FormControlLabel
            value={EXISTING_COVERAGE_MOVE_TYPE}
            control={<Radio />}
            label={MESSAGES.POLICY.MOVE_MODAL.EXISTING_COVERAGE_AND_COMBO_LABEL}
            data-testid={`radio-${EXISTING_COVERAGE_MOVE_TYPE}`}
            sx={{ mb: 1 }}
          />
          {moveType === EXISTING_COVERAGE_MOVE_TYPE && (
            <Box sx={{ pl: 4, pb: 2 }}>
              <TextField
                label="Coverage ID"
                value={existingCoverageId}
                onChange={(e) => handleExistingCoverageIdChange(e.target.value)}
                onBlur={() => validateExistingCoverage(true)}
                size="small"
                data-testid="coverage-id-input"
                sx={{ width: 220 }}
                error={!!existingCoverageValidationError}
                helperText={existingCoverageValidationError}
                slotProps={{
                  htmlInput: { maxLength: 8 },
                  input: {
                    endAdornment: existingCoverageId ? (
                      <InputAdornment position="end">
                        {existingCoverageQuery.isLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setExistingCoverageId('');
                              setExistingCoverageIdError('');
                            }}
                            aria-label="clear"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />

              {resolvedTargetCoverageName && resolvedTargetComboName && resolvedTargetComboId && (
                <Box sx={{ mt: 1.5 }} data-testid="existing-target-resolved">
                  <Typography variant="caption" color="text.secondary">Coverage name</Typography>
                  <Typography variant="body2" fontWeight={500}>{resolvedTargetCoverageName}</Typography>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    Combo ID
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>{resolvedTargetComboId}</Typography>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    Combo name
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>{resolvedTargetComboName}</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Option 2: New coverage in existing combo */}
          <FormControlLabel
            value="new-coverage"
            control={<Radio />}
            label={MESSAGES.POLICY.MOVE_MODAL.NEW_COVERAGE_IN_EXISTING_COMBO_LABEL}
            data-testid="radio-new-coverage"
            sx={{ mb: 1 }}
          />
          {moveType === 'new-coverage' && (
            <Box sx={{ pl: 4, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Combo ID"
                  value={targetComboId}
                  onChange={(e) => handleComboIdChange(e.target.value)}
                  onBlur={() => validateTargetCombo(true)}
                  size="small"
                  data-testid="combo-id-input"
                  sx={{ width: 220 }}
                  error={!!comboValidationError}
                  helperText={comboValidationError}
                  slotProps={{
                    htmlInput: { maxLength: 7 },
                    input: {
                      endAdornment: targetComboId ? (
                        <InputAdornment position="end">
                          {comboQuery.isLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTargetComboId('');
                                setUseLinkedCombo(false);
                                setComboIdError('');
                              }}
                              aria-label="clear"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useLinkedCombo}
                      onChange={(e) => handleUseLinkedComboChange(e.target.checked)}
                      data-testid="use-linked-combo-checkbox"
                    />
                  }
                  label={MESSAGES.POLICY.MOVE_MODAL.USE_LINKED_COMBO_LABEL}
                  sx={{ m: 0 }}
                />
              </Box>
              {/* Resolved Combo name */}
              {resolvedComboName && (
                <Box sx={{ mt: 1.5 }} data-testid="combo-name-resolved">
                  <Typography variant="caption" color="text.secondary">
                    Combo name
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {resolvedComboName}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Option 3: New coverage and combo */}
          <FormControlLabel
            value="new-combo"
            control={<Radio />}
            label={MESSAGES.POLICY.MOVE_MODAL.NEW_COVERAGE_AND_COMBO_LABEL}
            data-testid="radio-new-combo"
          />
        </RadioGroup>

        <Divider sx={{ my: 3, borderStyle: 'dashed' }} />

        {/* Reason textarea - always visible */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {MESSAGES.POLICY.MOVE_MODAL.REASON_LABEL}
            <Box component="span" sx={{ color: 'error.main' }}>
              *
            </Box>
          </Typography>
          {(moveType === 'existing' || moveType === 'new-coverage' || moveType === 'new-combo') && (
            <Typography
              variant="body2"
              sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}
              data-testid="system-note-prefix"
            >
              {systemNotePrefix}
            </Typography>
          )}
          <OutlinedInput
            value={reason}
            onChange={(e) => handleReasonChange(e.target.value)}
            onBlur={validateReason}
            multiline
            rows={4}
            fullWidth
            placeholder=""
            data-testid="move-policy-reason"
            error={!!reasonError}
          />
          {reasonError && (
            <Typography variant="caption" color="error" data-testid="move-policy-reason-error">
              {reasonError}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.8 }} />
            <Typography variant="body2" color="text.secondary">
              Consider including the context and reason for your action.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          data-testid="move-policy-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={handleSave}
          disabled={isSaving}
          data-testid="move-policy-save-btn"
          sx={{ bgcolor: 'grey.800', color: 'common.white', '&:hover': { bgcolor: 'grey.700' } }}
        >
          {isSaving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Save changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
